import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Descoberta de associados criados no SGA (at-32, Plano F).
 *
 * Alimenta o modal "Descoberta de Associados (SGA)" da tela de TI. Os dois
 * disparos rodam em segundo plano no backend e devolvem na hora o `runId`: o
 * progresso vem do polling do `status`, no mesmo desenho do modal de cotas.
 */

export type StatusExecucao = 'EM_ANDAMENTO' | 'CONCLUIDO' | 'FALHA' | 'INTERROMPIDO';
export type FaseDescoberta = 'LOCALIZACAO_PONTA' | 'DESCOBERTA' | 'BACKFILL';

export interface MarcadorDescoberta {
  /** Maior código de associado já varrido para frente. */
  ponta: number;
  /** Até onde o histórico já foi coberto pelo backfill. */
  backfillAte: number;
  pontaEm: string | null;
  /** false = a varredura nunca rodou; a primeira execução só localiza a ponta. */
  definido: boolean;
  historicoCoberto: boolean;
  percentualBackfill: number;
}

export interface ExecucaoDescoberta {
  id: string;
  status: StatusExecucao;
  origem: 'CRON' | 'MANUAL';
  fase: FaseDescoberta;
  total: number;
  consultadas: number;
  percentual: number;
  codigosVarridos: number;
  associadosImportados: number;
  veiculosImportados: number;
  foraDeCarteira: number;
  jaConhecidos: number;
  falhas: number;
  exemplosFalhas: string[];
  marcadorFinal: number | null;
  erro: string | null;
  iniciadoEm: string;
  finalizadoEm: string | null;
}

export interface StatusDescoberta {
  marcador: MarcadorDescoberta;
  associadosImportadosDoSga: number;
  execucao: ExecucaoDescoberta | null;
}

export interface DisparoDescoberta {
  mensagem: string;
  runId: string;
}

export const getStatusDescoberta = async (): Promise<StatusDescoberta> => {
  try {
    const { data } = await http.get<StatusDescoberta>('/sga-descoberta/status');
    return data;
  } catch (error) {
    throw new Error(
      extrairMensagemErro(error, 'Não foi possível consultar o status da descoberta.'),
    );
  }
};

export const dispararDescoberta = async (): Promise<DisparoDescoberta> => {
  try {
    const { data } = await http.post<DisparoDescoberta>('/sga-descoberta/executar');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível iniciar a varredura.'));
  }
};

export const dispararBackfillDescoberta = async (): Promise<DisparoDescoberta> => {
  try {
    const { data } = await http.post<DisparoDescoberta>('/sga-descoberta/backfill');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível iniciar a carga inicial.'));
  }
};

/** Interrompe a carga inicial em andamento (para na próxima fronteira de lote). */
export const pararBackfillDescoberta = async (): Promise<{
  interrompido: boolean;
  mensagem: string;
}> => {
  const { data } = await http.post<{ interrompido: boolean; mensagem: string }>(
    '/sga-descoberta/backfill/parar',
  );
  return data;
};
